import {
  FOSTER_SPONSOR,
  INTERNATIONAL_SPONSOR,
  PLATINUM_AND_SPONSOR,
  PLATINUM_FOSTER_SPONSOR,
  PLATINUM_SPONSOR,
  SPONSOR,
} from "../../../../common/constants/sponsor-constants";
import ABOName from "../../ABOName/ABOName";
import SponsorTypeLabel from "../SponsorTypeLabel/SponsorTypeLabel";
import ABOPhoneNumber from "../../ABOPhoneNumber/ABOPhoneNumber";
import ABOEmail from "../../ABOEmail/ABOEmail";
import ABOAddress from "../../ABOAddress/ABOAddress";
import "./SponsorDetail.scss";
import { cardActionClickTrack } from "../../../../common/tealium/ABOProfile/Profile/ProfileTabViewsTrack/SponsorTabTrack";
import { storedLocale } from "../../../Authentication/util";
import { useTranslation } from "react-i18next";
import { getDisplayName } from "../../../../common/util/displayName";
import { useAppSelector } from "../../../../store";

type SponsorPhone = {
  displayNumber: string;
  extension: string;
  number: string;
  outOfCountryCallingNumber: string;
};

type SponsorAddress = {
  type: string;
  addressLines: string[];
  municipality: string;
  region: string;
  postalCode: string;
  country: string;
};

type Props = {
  sponsor: {
    aboNo: string;
    affNo: string;
    isValid: boolean;
    isPlatinum?: boolean;
    name?: string;
    nameLocal?: string;
    displayName?: string;
    privacyFlag?: boolean;
    phone?: SponsorPhone;
    email?: string;
    address?: SponsorAddress;
  };
  show: boolean;
  sponsorType:
    | typeof SPONSOR
    | typeof PLATINUM_SPONSOR
    | typeof PLATINUM_AND_SPONSOR
    | typeof INTERNATIONAL_SPONSOR
    | typeof FOSTER_SPONSOR
    | typeof PLATINUM_FOSTER_SPONSOR;
};

const SponsorDetail = (props: Props) => {
  const { sponsor, show, sponsorType } = props;
  const { aboNo } = sponsor;
  const { t } = useTranslation(["common"]);
  const boot = useAppSelector(({ boot }) => boot);
  const hideSponsorAddress = boot?.configuration?.profile?.hideSponsorAddress;

  const applicantTrackOnclick = (clickAction: string) => {
    cardActionClickTrack(clickAction, sponsorType);
  };

  if (show === false) {
    return <></>;
  }

  // Use displayName if available (backend-resolved), otherwise fall back to name/localName
  let sponsorDisplayName: string;
  if (sponsor.displayName) {
    // Backend already resolved the display name, just translate special values
    sponsorDisplayName = getDisplayName(sponsor.displayName, t);
  } else {
    // Fallback: use ABOName component logic with name/localName
    sponsorDisplayName = ""; // Will use ABOName component instead
  }

  const privacyFlag = sponsor.privacyFlag ?? false;
  const primaryNumber = sponsor.phone?.displayNumber ?? "";
  const email = sponsor.email ?? "";
  const address = sponsor.address ?? {
    addressLines: [] as string[],
    municipality: "",
    region: "",
    postalCode: "",
    country: "",
    type: "",
  };

  return (
    <div className="leaf-sponsor-detail">
      <div className="leaf-sponsor-detail__header">
        <div className="leaf-sponsor-detail__header--details">
          {sponsor.displayName ? (
            <span className="abo-name">{sponsorDisplayName}</span>
          ) : (
            <ABOName
              name={sponsor.name ?? ""}
              localName={sponsor.nameLocal ?? ""}
              privacyFlag={undefined}
              languageCode=""
              locale={storedLocale()}
            />
          )}
          <div className="leaf-sponsor-detail__header--abo-number">{aboNo}</div>
        </div>
        <div data-testid="leafSponsorDetail-sponsor-type">
          <SponsorTypeLabel sponsorType={sponsorType} />
        </div>
      </div>
      <div className="leaf-sponsor-detail__body">
        <ABOPhoneNumber
          primaryNumber={primaryNumber}
          privacyFlag={privacyFlag}
          applicantTrackOnclick={applicantTrackOnclick}
        />
        <ABOEmail email={email} privacyFlag={privacyFlag} applicantTrackOnclick={applicantTrackOnclick} />
        {!hideSponsorAddress && (
          <ABOAddress
            addresses={{
              addressLines: address.addressLines,
              municipality: address.municipality,
              region: address.region,
              postalCode: address.postalCode,
              country: address.country,
              type: address.type,
            }}
            hideCopyIcon={true}
            privacyFlag={privacyFlag}
            applicantTrackOnclick={applicantTrackOnclick}
          />
        )}
      </div>
    </div>
  );
};

export default SponsorDetail;
